import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import SymbolButton from 'deprecated-shared-ui/templates/symbol-button/default';
import HeroPicture from './AlpiHeroPicture.jsx';
import Landmark from '@core/scripts/components/landmark-adapter';
import Device from '@core/scripts/components/device';
import {Constants} from '../view-constants';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import {yieldToMain} from "../ArticleOverviewHelper.js";
import {buildAlpiJshKey} from '@article-overview/utils/build-alpi-jsh-key.js';

//5 is the good value for recognizing finger movements on the touch screen as swipe movements
const SWIPER_THRESHOLD_RIGHT = 15;
const SWIPER_THRESHOLD_LEFT = -15;
const MAX_DIFF_TOUCH_Y = 50;

function HeroPictureSlider({
                                alpiId,
                               url,
                               images,
                               positionIndex,
                               inView,
                               isShopContent,
                               heroPictureOnClick,
                               onImageLoaded,
                               heroPictureOnAuxClick,
                               heroPictureOnContextMenu,
                           }) {
    const [slideIndex, setSlideIndex] = useState(0);
    const [touch, setTouch] = useState({startX: null, startY: null, endX: null, endY: null});
    const transitionEnabledRef = useRef(true);
    const isButtonClickIgnored = useRef(false);

    const {browser, texts} = useContext(articleOverviewStaticContext);
    const previousImages = useRef(images);
    const isIE = browser === 'IE';
    useEffect(() => {
        if (!transitionEnabledRef.current) {
            //enable button click
            isButtonClickIgnored.current = false;
            //reactivate transition after reinitialization of image loop
            if (slideIndex !== -1 && slideIndex !== images.length && images.length !== 1) {
                transitionEnabledRef.current = true;
            }
        }
        // timeout corresponds to transition duration in slider.scss and should have the similar value for properly transition of slider images in IE
        if (isIE) {
            setTimeout(handleTransitionEnd, 160);
        }
    }, [slideIndex]);

    handleColorChange();

    function handleColorChange() {
        if (previousImages.current !== images && previousImages.current.length !== images.length) {
            previousImages.current = images;
            if (images.length > 1) {
                transitionEnabledRef.current = true;
            }
            // if there are fewer pictures for next color, the latter picture of previous color will be shown
            if (slideIndex > images.length - 1) {
                transitionEnabledRef.current = false;
                setSlideIndex(images.length - 1);
            }
        }
    }

    function onSliderButtonClick(isSlideBack) {
        //to prevent button clicks that are faster than transition
        if (isButtonClickIgnored.current) {
            return;
        }
        slideToSpecificImage(findNewImageIndex(isSlideBack));
    }

    function slideToSpecificImage(index) {
        yieldToMain(() => {
            isButtonClickIgnored.current = true;
            setSlideIndex(index)
            sendLandmark();
        });
    }

    function findNewImageIndex(isSlideBack) {
        return isSlideBack ? slideIndex - 1 : slideIndex + 1;
    }

    const handleTouchStart = useCallback((clientX, clientY) => {
        setTouch((prevTouch) => ({...prevTouch, startX: clientX, startY: clientY}));
    }, []);

    const handleTouchMove = useCallback((clientX, clientY) => {
        setTouch((prevTouch) => ({...prevTouch, endX: clientX, endY: clientY}));
    }, []);

    const handleTouchEnd = useCallback(() => {
        const diffX = touch.startX - touch.endX;
        const diffY = touch.startY - touch.endY;
        if (diffX > SWIPER_THRESHOLD_RIGHT
            && Math.abs(diffY) < MAX_DIFF_TOUCH_Y
            && images.length > 1
            && Math.abs(diffX) > Math.abs(diffY)) {
            slideToSpecificImage(findNewImageIndex(false));
        }
        if (
            diffX < SWIPER_THRESHOLD_LEFT &&
            Math.abs(diffY) < MAX_DIFF_TOUCH_Y &&
            images.length > 1 &&
            Math.abs(diffX) > Math.abs(diffY)
        ) {
            slideToSpecificImage(findNewImageIndex(true));
        }
        setTouch({startX: null, startY: null, endX: null, endY: null});
    }, [touch, images.length]);

    function handleTransitionEnd() {
        isButtonClickIgnored.current = false;
        if (slideIndex === -1) {
            transitionEnabledRef.current = false;
            setSlideIndex(images.length - 1);
        } else if (slideIndex === images.length) {
            transitionEnabledRef.current = false;
            setSlideIndex(0);
        }
    }

    function sendLandmark() {
        const pageType = isShopContent ? Constants.LANDMARK_PAGETYPE_AOP : Constants.LANDMARK_PAGETYPE_SRP;
        const lmValue = `${pageType}\\${Constants.LANDMARK_PICSLIDE}`;

        Landmark.sendLandmark({
            landmark: 'userinterface',
            click: lmValue,
        });
    }

    const sliderContentStyle = {
        transform: `translateX(-${(slideIndex + 1) * 100}%)`,
    };
    const firstImage = images[0];
    const lastImage = images[images.length - 1];

    const slidable = images.length > 1;

    const currentSlideIndex = (slideIndex + images.length) % images.length;

    const sliderHook = `jsh-alpi-slider-anchor-${buildAlpiJshKey(alpiId, positionIndex)}`;
    const attributes = {[sliderHook]: ''};

    const onClick = (e, image) => {
        return heroPictureOnClick(e, url, image.id);
    };
    const onAuxClick = (e, linkTag, image) => {
        return heroPictureOnAuxClick(e, linkTag, url, image.id);
    };
    const onContextMenu = (linkTag, image) => {
        return heroPictureOnContextMenu(linkTag, url, image.id);
    };

    return (
        <>
            <div data-testid="hero-picture-slider"
                 className="slider__content-wrapper"
                 onTouchStart={(e) => {handleTouchStart(e.targetTouches[0].clientX, e.targetTouches[0].clientY)}}
                 onTouchMove={(e) => {handleTouchMove(e.targetTouches[0].clientX, e.targetTouches[0].clientY)}}
                 onTouchEnd={handleTouchEnd}
                 {...attributes}
            >
                <div
                    className={!transitionEnabledRef.current ? 'slider__content' : 'slider__content transition-active'}
                    style={sliderContentStyle}
                    onTransitionEnd={handleTransitionEnd}
                >
                    {/* last image additionally at position -1 (left to the swiper) */}
                    <HeroPicture
                        url={url}
                        image={lastImage}
                        inView={inView && slideIndex === -1}
                        heroPictureOnClick={(e) => onClick(e, lastImage)}
                        heroPictureOnAuxClick={(e, linkTag) => onAuxClick(e, linkTag, lastImage)}
                        heroPictureOnContextMenu={(linkTag) => onContextMenu(linkTag, lastImage)}
                        onLoad={onImageLoaded}
                    />
                    {images.map((image, index) => (
                        <HeroPicture
                            key={index}
                            url={url}
                            image={image}
                            inView={inView && image === images[slideIndex]}
                            heroPictureOnClick={(e) => onClick(e, image)}
                            heroPictureOnAuxClick={(e, linkTag) => onAuxClick(e, linkTag, image)}
                            heroPictureOnContextMenu={(linkTag) => onContextMenu(linkTag, image)}
                            onLoad={onImageLoaded}
                        />
                    ))}

                    {/* first image additionally behind the swiper (right to the swiper) */}
                    <HeroPicture
                        url={url}
                        image={firstImage}
                        inView={inView && slideIndex === images.length}
                        heroPictureOnClick={(e) => onClick(e, firstImage)}
                        heroPictureOnAuxClick={(e, linkTag) => onAuxClick(e, linkTag, firstImage)}
                        heroPictureOnContextMenu={(linkTag) => onContextMenu(linkTag, firstImage)}
                        onLoad={onImageLoaded}
                    />
                </div>
            </div>
            {Device.isTouch() && slidable && (
                <div className="slider__bullets-wrapper">
                    {images.map((image, index) => (
                        <div
                            key={image.id}
                            className={currentSlideIndex === index ? 'slider__bullet active' : 'slider__bullet'}
                            onClick={() => slideToSpecificImage(index)}
                        ></div>
                    ))}
                </div>
            )}
            {!Device.isTouch() && slidable && (
                <div className="slider__image-slider-button prev-button">
                    <SymbolButton
                        icon="arrow-left"
                        label={texts?.["prevPage"]}
                        onClick={() => onSliderButtonClick(true)}
                    />
                </div>
            )}
            {!Device.isTouch() && slidable && (
                <div className="slider__image-slider-button next-button">
                    <SymbolButton
                        icon="arrow-right"
                        label={texts?.["nextPage"]}
                        onClick={() => onSliderButtonClick(false)}
                    />
                </div>
            )}
        </>
    );
}

export default React.memo(HeroPictureSlider);
